import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Helper text (Flutter `helperText`). Hidden while an error is shown. */
  hint?: string;
  /** Error text (Flutter `errorText`). */
  error?: string;
  /** Flutter `prefixIcon`: a 48px box at the start of the field. */
  leftIcon?: ReactNode;
  /** Flutter `suffixIcon`: a 48px box at the end of the field; its content stays interactive. */
  rightIcon?: ReactNode;
}

/**
 * Outlined + filled TextField exactly as apps/mobile's InputDecorationTheme draws it:
 * 56px tall, surfaceContainer fill, 12px radius, 1px outlineVariant border (primary when
 * focused, error when invalid) and a label that floats onto the border, notching it.
 *
 * The notch is the native <fieldset>/<legend> technique; label and legend react to the
 * input through the `peer` selectors (focused, or not showing its placeholder = has a value).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, hint, error, leftIcon, rightIcon, className, type = 'text', disabled, id, placeholder, ...props },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;
    const hasError = !!error;

    return (
      <div data-testid="input-root" className={clsx('w-full', className)}>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={type}
            disabled={disabled}
            // A non-empty placeholder is what lets CSS tell "empty" from "has a value".
            placeholder={placeholder || ' '}
            aria-describedby={clsx(hint && !hasError && hintId, hasError && errorId) || undefined}
            aria-invalid={hasError}
            className={clsx(
              'peer relative block h-14 w-full rounded-xl bg-lm-surface-container text-body-lg text-lm-on-surface caret-lm-primary outline-none',
              'placeholder:text-transparent focus:placeholder:text-lm-on-surface-variant',
              'disabled:text-lm-on-surface/38',
              'px-3',
              leftIcon && 'pl-12',
              rightIcon && 'pr-12'
            )}
            {...props}
          />

          {/* Notched border. The empty legend collapses to nothing until the label floats. */}
          <fieldset
            aria-hidden="true"
            data-testid="input-outline"
            className={clsx(
              'pointer-events-none absolute inset-x-0 bottom-0 -top-[5px] m-0 min-w-0 rounded-xl border px-2',
              hasError
                ? 'border-lm-error peer-focus:border-lm-error'
                : 'border-lm-outline-variant peer-focus:border-lm-primary',
              'peer-disabled:border-lm-on-surface/12',
              // Open the notch while the label is floating.
              'peer-focus:[&>legend]:max-w-full peer-[:not(:placeholder-shown)]:[&>legend]:max-w-full'
            )}
          >
            <legend className="invisible block h-[11px] max-w-[0.01px] whitespace-nowrap p-0 text-[12px] transition-[max-width] duration-100">
              <span className="px-1">{label}</span>
            </legend>
          </fieldset>

          {label && (
            <label
              htmlFor={inputId}
              className={clsx(
                'pointer-events-none absolute top-1/2 origin-left -translate-y-1/2 whitespace-nowrap text-body-lg transition-all duration-150',
                leftIcon ? 'left-12' : 'left-3',
                // Floating state: focused or holding a value.
                'peer-focus:left-3 peer-focus:top-0 peer-focus:scale-75',
                'peer-[:not(:placeholder-shown)]:left-3 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:scale-75',
                hasError
                  ? 'text-lm-error peer-focus:text-lm-error'
                  : 'text-lm-on-surface-variant peer-focus:text-lm-primary',
                'peer-disabled:text-lm-on-surface/38'
              )}
            >
              {label}
            </label>
          )}

          {leftIcon && (
            <div
              className={clsx(
                'pointer-events-none absolute left-0 top-1 flex h-12 w-12 items-center justify-center text-lm-on-surface-variant',
                disabled && 'opacity-38'
              )}
            >
              {leftIcon}
            </div>
          )}
          {rightIcon && (
            <div
              className={clsx(
                'absolute right-0 top-1 flex h-12 w-12 items-center justify-center text-lm-on-surface-variant',
                disabled && 'opacity-38'
              )}
            >
              {rightIcon}
            </div>
          )}
        </div>

        {hasError ? (
          <p id={errorId} className="mt-1 px-3 text-body-sm text-lm-error" role="alert">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="mt-1 px-3 text-body-sm text-lm-on-surface-variant" role="status">
              {hint}
            </p>
          )
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
